import { Tag, H2, H3, SubBody, TertiaryButton, Bold, Lead, PrimaryButton, Meta } from '@rikstv/shared-components';
import { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackSelectInternetBundle, trackClickInternetCTA } from '~/analytics/eventTracker';
import { DialogCloseButton } from '~/components/shared/dialog/dialog-close-button';
import { useDialog } from '~/components/shared/dialog/useDialog';
import { FlexBox } from '~/components/shared/layout/FlexBox';
import { useIsMobileView } from '~/hooks/useMediaQuery';
import { InternetBundle, toProductBundle } from '../internet.utils';
import { useInternetFlowStore, InternetCustomerAddress } from '../useInternetStore';
import { ReactComponent as ChannelsSvg } from '~/components/shared/subscription-comparison/images/feature-icons/channels.svg';
import { ReactComponent as InternetSvg } from '~/components/shared/subscription-comparison/images/feature-icons/internet.svg';
import { ReactComponent as StreamingServiceSvg } from '~/components/shared/subscription-comparison/images/feature-icons/tv.svg';

import classes from '../InternetOfferSubPage.module.css';
import { DefaultBlockContent } from '~/components/shared';

export const InternetBundleCard: FC<{ bundle: InternetBundle }> = ({ bundle }) => {
  const dialog = useDialog();
  const isMobile = useIsMobileView();
  const selectedAddress = useInternetFlowStore((s) => s.selectedAddress) as InternetCustomerAddress;
  const setSelectedInternetProduct = useInternetFlowStore((s) => s.setSelectedInternetProduct);
  const navigate = useNavigate();
  const onSelectPackage = () => {
    trackSelectInternetBundle({
      bundle: { name: bundle.name, price: bundle.price },
      city: selectedAddress.city,
      postal_code: selectedAddress.postalCode,
    });
    setSelectedInternetProduct(toProductBundle(bundle));
    navigate(bundle.subscription.campaignUrl);
  };

  return (
    <div className={classes.packageCard}>
      <Tag className={classes.purpleHat}>Spar {bundle.savingsPercentage}%</Tag>
      <FlexBox direction="column" alignItems={'center'} gap={'24px'}>
        {/* title */}
        <H2 lookLike="title-3">{bundle.name}</H2>
        {/* price */}
        <div>
          <FlexBox alignItems={'end'}>
            <H3 lookLike="title-2" title={`${bundle.subscription.currentPrice},- + ${bundle.ngtProduct.productPrice},-`}>
              {bundle.price},-
            </H3>
            <SubBody>/mnd. i 12 mnd*</SubBody>
          </FlexBox>
          <Meta
            as="div"
            className={classes.centerText}
            title={`${bundle.subscription.originalPrice},- + ${bundle.ngtProduct.originalProductPrice},-`}
          >
            Originalpris {bundle.originalPrice},- /mnd
          </Meta>
        </div>
        {/* features */}
        <Feature icon={<InternetSvg />} label="Internett**" feature={bundle.ngtProduct.productName} />
        <Feature icon={<ChannelsSvg width="28px" />} label="Antall kanaler" feature={bundle.features.channels} />
        <Feature icon={<StreamingServiceSvg />} label="Antall strømmetjenester" feature={bundle.features.streamingServices.length} />
        {/* actions */}
        <div>
          <FlexBox>
            <TertiaryButton
              onClick={() => {
                trackClickInternetCTA({ link_text: `Les mer om ${bundle.name}`, ngt_available: true });
                dialog.openModalDialog();
              }}
            >
              Les mer
            </TertiaryButton>
            <dialog ref={dialog.ref}>
              <FlexBox direction="column" gap="1.5rem">
                <FlexBox direction="column">
                  <H3>{bundle.name}</H3>
                  <Bold>{bundle.subscription.name} inkludert</Bold>
                  <ul style={{ paddingLeft: '1.5rem' }}>
                    <li>{bundle.features.channels} TV-kanaler</li>
                    <li>Strømmetjenestene {bundle.features.streamingServices.join(', ')}</li>
                  </ul>
                  <DefaultBlockContent useRdsComponents blocks={bundle.sanityProduct.description} />
                </FlexBox>
                <FlexBox
                  direction={isMobile ? 'column' : undefined}
                  alignItems="center"
                  justifyContent={isMobile ? undefined : 'space-between'}
                >
                  <div>
                    <Lead style={{ '--rds-background-primary': '#00816D' } as any}>{bundle.price},- /mnd i 12 mnd</Lead>
                    <Meta as="div" className={isMobile ? classes.centerText : undefined}>
                      Originalpris {bundle.originalPrice},- /mnd
                    </Meta>
                  </div>
                  <PrimaryButton data-testid={`modal-select-${bundle.subscription.name}`} onClick={onSelectPackage}>
                    Bestill nå
                  </PrimaryButton>
                </FlexBox>
              </FlexBox>
              <DialogCloseButton onClick={() => dialog.closeDialog()} />
            </dialog>
            <PrimaryButton data-testid={`select-${bundle.subscription.name}`} onClick={onSelectPackage}>
              Bestill nå
            </PrimaryButton>
          </FlexBox>
        </div>
      </FlexBox>
    </div>
  );
};

const Feature: FC<{ icon: ReactNode; label: string; feature: string | number }> = ({ icon, label, feature }) => (
  <div style={{ width: '100%' }}>
    <FlexBox justifyContent="space-between" style={{ borderBottom: '1px solid #DDD', paddingBlock: '8px' }}>
      <FlexBox>
        {icon}
        <span>{label}</span>
      </FlexBox>
      <span>{feature}</span>
    </FlexBox>
  </div>
);
